import { FunctionComponent, useEffect, useRef, useState } from "react";
import Table from "../../Components/Table/Table";
import CustomToolbar from "../../Components/Table/components/CustomToolbar";
import ButtonField from "../../Components/Button/ButtonField";
import { Columns } from "../../Components/Table/ITable";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import Dialog from "../../Components/Dialog/Dialog";
import CourseTutorMappingForm from "./CourseTutorMappingForm";
import ResponseDialog from "../../Components/ResponseDialog/ResponseDialog";
import TableSearch from "../../Components/Table/components/TableSearch";
import { ReactComponent as Delete } from "../../Assets/Images/delete.svg";
import { ReactComponent as Edit } from "../../Assets/Images/edit.svg";
import { IDeleteDialog } from "../../Components/DeleteDialog/IDeleteDialog";
import IconButtonField from "../../Components/Button/IconButtonField";
import { SvgIcon } from "@mui/material";
import DeleteDialog from "../../Components/DeleteDialog/DeleteDialog";
import { ReactComponent as RefreshButton } from "../../Assets/Images/RefreshButton.svg";
import { getCourseTutorMapping, createEditCourseTutorMappingAction, getAvailableTutors } from "../../Redux/Slices/CourseTutorMapping/CourseTutorMappingApi";
import { clearCourseTutorMappingResponse } from "../../Redux/Slices/CourseTutorMapping/CourseTutorMappingSlice";
import { ISideMenu } from "../../Redux/Slices/SideMenuPermission/ListSideMenuPermissionSlice";
import { Typography } from "@mui/material";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";

interface CourseTutorMappingProps {}

interface ICourseTutorMapping {
    course: {
        id: number;
        course_id: string;
        full_name: string;
        short_name: string;
    };
    tutors: {
        id: number;
        email: string;
        username?: string;
        first_name?: string;
        last_name?: string;
    }[];
    mapping_count: number;
}

const CourseTutorMapping: FunctionComponent<CourseTutorMappingProps> = () => {
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [deleteDialogData, setDeleteData] = useState<IDeleteDialog>({});
    const [initialValues, setInitialValues] = useState<ICourseTutorMapping | {}>({
        status: true,
    });
    const dispatch = useAppDispatch();
    const { data: courseTutorMappingData, saveData: saveResponse } = useAppSelector(
        (state) => state.CourseTutorMapping
    );

    // user permission logic (pattern from Role.tsx)
    const userPermissionlist = useAppSelector(
        (state) => (state.SideMenuPermission as ISideMenu).data
    );

    const to = "settings";
    const filteredList = userPermissionlist?.Dashboard?.filter(
        (item: any) => item.to === to
    ) || [];
    const fliterdata: any = filteredList[0]?.items || [];
    const finaldata = fliterdata.find((item: any) => item.to === "course-tutor-mapping") || fliterdata[0];

    const availableTutorData = useAppSelector((state) => state.CourseTutorMapping?.availableTutorData);

    // Transform ICourseTutorMapping to ICourseTutorMappingForm
    const transformToFormData = (mapping: ICourseTutorMapping | {}): any => {
        if (!mapping || !('course' in mapping)) {
            return { status: true, tutor_ids: [] };
        }
        
        const typedMapping = mapping as ICourseTutorMapping;
        const tutorOptions: AutocompleteOption[] = Array.isArray(availableTutorData?.data)
            ? availableTutorData!.data.map((tutor: any) => ({
                value: tutor.id ?? tutor.tutor_id,
                label: tutor.student_name || tutor.full_name || tutor.name || tutor.username || 'Unknown',
            }))
            : [];

        // Map the array of tutors to their IDs
        const mappingTutorIds = typedMapping.tutors?.map(t => t.id) || [];

        const selectedTutors = tutorOptions.filter((opt: AutocompleteOption) => 
            mappingTutorIds.includes(opt.value as number)
        );

        return {
            course_tutor_map_id: "0",
            course_id: typedMapping.course?.course_id || "",
            tutor_ids: selectedTutors,
            status: true,
        };
    };

    const formRef = useRef<HTMLFormElement>(null);
    const columns: Columns = [
        {
            field: "index",
            renderHeader: () => <b>S.No</b>,
            width: 90,
            headerAlign: "center",
            align: "center",
        },
        // {
        //     field: "course_id",
        //     renderHeader: () => <b>Course ID</b>,
        //     flex: 1,
        //     type: "string",
        //     headerAlign: "left",
        //     renderCell: (params) => (
        //         <Typography
        //             variant="body2"
        //             sx={{
        //                 fontFamily: "monospace",
        //                 backgroundColor: "#f0f0f0",
        //                 padding: "4px 8px",
        //                 borderRadius: 1,
        //                 display: "inline-block",
        //             }}
        //         >
        //             {params.value || "-"}
        //         </Typography>
        //     ),
        // },
        {
            field: "course_name",
            renderHeader: () => <b>Course Name</b>,
            flex: 1.5,
            type: "string",
            headerAlign: "left",
            valueGetter: (params) => params.row.course?.full_name || "-",
        },
        {
            field: "tutor_names",
            renderHeader: () => <b>Tutor Names</b>,
            flex: 2,
            type: "string",
            headerAlign: "left",
            valueGetter: (params) => {
                const tutors = params.row.tutors;
                if (Array.isArray(tutors) && tutors.length > 0) {
                    return tutors.map((t: any) => `${t.first_name || ""} ${t.last_name || ""}`.trim() || t.username || t.email).join(", ");
                }
                return "-";
            },
        },
        {
            field: "tutor_emails",
            renderHeader: () => <b>Tutor Emails</b>,
            flex: 2,
            type: "string",
            headerAlign: "left",
            valueGetter: (params) => {
                const tutors = params.row.tutors;
                if (Array.isArray(tutors) && tutors.length > 0) {
                    return tutors.map((t: { email: string }) => t.email).join(", ");
                }
                return "-";
            },
        },
        {
            field: "status",
            renderHeader: () => <b>Status</b>,
            flex: 1,
            type: "string",
            headerAlign: "left",
            cellClassName: () => `activename active`,
            valueFormatter: () => "Active",
        },
        {
            field: "action",
            renderHeader: () => <b>Action</b>,
            flex: 1,
            type: "actions",
            renderCell: (params: GridRenderCellParams<ICourseTutorMapping>) => {
                return (
                    <>
                        <IconButtonField
                            disabled={finaldata?.permissions?.edit === false}
                            onClick={() => {
                                setInitialValues({ ...params.row });
                                setDialogOpen(true);
                            }}
                        >
                            <SvgIcon sx={{ fill: "none" }} component={Edit} inheritViewBox />
                        </IconButtonField>
                        <IconButtonField
                            disabled={finaldata?.permissions?.delete === false}
                            onClick={() => {
                                 setDeleteData({ 
                                    id: "0", 
                                    name: `${params.row.course?.full_name} - ${params.row.tutors?.map(t => t.username || t.first_name || t.email).join(", ") || "Tutors"}`,
                                    course_id: params.row.course?.course_id,
                                    tutor_ids: params.row.tutors?.map(t => t.id) || []
                                 });
                                setDeleteDialogOpen(true);
                            }}
                        >
                            <SvgIcon component={Delete} inheritViewBox />
                        </IconButtonField>
                    </>
                );
            },
        },
    ];

    const courseTutorMappings = Array.isArray(courseTutorMappingData?.data)
        ? courseTutorMappingData?.data.map((mapping: ICourseTutorMapping, index: number) => ({
            ...mapping,
            index: index + 1,
        }))
        : [];

    useEffect(() => {
        dispatch(getCourseTutorMapping());
        dispatch(getAvailableTutors());
    }, [dispatch]);

    useEffect(() => {
        if (saveResponse?.status) {
            dispatch(getCourseTutorMapping());
            if (dialogOpen) setDialogOpen(false);
            if (deleteDialogOpen) setDeleteDialogOpen(false);
        }
    }, [saveResponse?.status, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(clearCourseTutorMappingResponse());
        };
    }, [dispatch]);

    const handleDelete = () => {
        const deleteData = {
            course_tutor_map_id: deleteDialogData.id || "0",
            course_id: deleteDialogData.course_id || "",
            tutor_ids: deleteDialogData.tutor_ids || [],
            status: false,
            is_delete: true
        };
        dispatch(createEditCourseTutorMappingAction(deleteData));
    };

    return (
        <>
            <Table
                rows={courseTutorMappings || []}
                columns={columns}
                exportFileName="Course Tutor Mappings"
                getRowId={(row) => row.course?.course_id || row.index}
                sx={{
                    boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)",
                    borderRadius: 2,
                }}
            >
                <CustomToolbar>
                    <Typography variant="h6" component="h6"></Typography>
                    <TableSearch />
                    <ButtonField
                        className="tabel_add_new_button"
                        onClick={() => {
                            setInitialValues({ status: true });
                            setDialogOpen(true);
                        }}
                        toolTip="Add new"
                        disabled={finaldata?.permissions?.add === false}
                    >
                        Add new
                    </ButtonField>
                    <IconButtonField
                        className="refresh_btn"
                        toolTip="Refresh"
                        onClick={() => dispatch(getCourseTutorMapping())}
                    >
                        <SvgIcon
                            className="refresh_svg_icon"
                            component={RefreshButton}
                            inheritViewBox
                        />
                    </IconButtonField>
                </CustomToolbar>
            </Table>

            <Dialog
                open={dialogOpen}
                dialogClassName="dialog"
                Dialog_Title={`${(initialValues as ICourseTutorMapping).course ? "Update" : "Add"} Course Tutor Mapping`}
                dialogContentClassName="dialog_content"
                Dialog_Content={
                    dialogOpen ? (
                        <CourseTutorMappingForm
                            formRef={formRef}
                            initialValues={transformToFormData(initialValues)}
                            setInitialValues={setInitialValues}
                        />
                    ) : undefined
                }
                Dialog_Actions={
                    <>
                        <ButtonField
                            className="form_cancel_button"
                            onClick={() => setDialogOpen(false)}
                        >
                            Cancel
                        </ButtonField>
                        <ButtonField
                            className="form_save_button"
                            onClick={() => formRef.current?.requestSubmit()}
                        >
                            Save
                        </ButtonField>
                    </>
                }
            />
            <ResponseDialog 
                response={saveResponse} 
                onOkClick={() => {
                    dispatch(clearCourseTutorMappingResponse());
                }}
            />
            <DeleteDialog
                deleteableName={deleteDialogData.name ?? ""}
                open={deleteDialogOpen}
                setOpen={setDeleteDialogOpen}
                deleteFunction={handleDelete}
            />
        </>
    );
};

export default CourseTutorMapping;
